import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { grnAPI, itemAPI, unitAPI, categoryAPI, getBillUrl, downloadBill, viewBill, grnReconciliationAPI } from '../lib/api';
import { compressImageFile } from '../lib/imageCompress';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { DataTable } from '../components/DataTable';
import { SearchableItemSelect } from '../components/SearchableItemSelect';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ClipboardCheck, 
  Loader2, 
  Eye,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Download,
  Upload,
  Pencil,
  Save,
  Plus,
  Trash2,
  Merge,
  Receipt,
  IndianRupee,
  TrendingUp,
  Calendar,
  Package,
  RefreshCw,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '../lib/utils';
import { Checkbox } from '../components/ui/checkbox';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

// Stock Verification Tab Component
const StockVerificationTab = () => {
  const [verificationData, setVerificationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedGRN, setExpandedGRN] = useState(null);

  const fetchVerification = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);
      
      const response = await grnReconciliationAPI.getStockVerification();
      setVerificationData(response.data);
    } catch (error) {
      console.error('Failed to fetch stock verification:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchVerification();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const summary = verificationData?.summary || {};
  const grnWise = verificationData?.grn_wise_verification || [];
  const finalVerification = verificationData?.final_stock_verification || [];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">GRNs Verified</p>
                <p className="text-2xl font-bold">{summary.total_grns_verified || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <ClipboardCheck className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Items in GRNs</p>
                <p className="text-2xl font-bold">{summary.total_items_in_grns || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Package className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Qty from GRNs</p>
                <p className="text-2xl font-bold">{summary.total_quantity_from_grns || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Verification Status</p>
                <p className={`text-lg font-bold ${summary.all_verified ? 'text-green-600' : 'text-amber-600'}`}>
                  {summary.all_verified ? 'All Verified ✓' : `${summary.items_with_mismatches} Mismatch(es)`}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => fetchVerification(true)} disabled={refreshing}>
                {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* GRN-wise Verification */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            GRN-wise Stock Addition
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Shows items added to stock via each GRN with before/after quantities
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {grnWise.map((grn, index) => (
              <div key={grn.grn_id} className="border rounded-lg overflow-hidden">
                {/* GRN Header - Clickable */}
                <div 
                  className="p-4 bg-muted/30 flex items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setExpandedGRN(expandedGRN === grn.grn_id ? null : grn.grn_id)}
                >
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="font-semibold text-primary">{grn.grn_number}</p>
                      <p className="text-xs text-muted-foreground">PO: {grn.po_number}</p>
                    </div>
                    <Badge variant="secondary">{grn.grn_date}</Badge>
                    <span className="text-sm text-muted-foreground">{grn.vendor_name}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium">{grn.total_items} items</p>
                      <p className="text-xs text-muted-foreground">+{grn.total_quantity_added} qty added</p>
                    </div>
                    <Badge variant="outline" className="text-green-600 border-green-600">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Added to Stock
                    </Badge>
                    {expandedGRN === grn.grn_id ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                </div>

                {/* GRN Items - Expandable */}
                {expandedGRN === grn.grn_id && (
                  <div className="border-t">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/20">
                          <TableHead>Item</TableHead>
                          <TableHead className="text-right">Stock Before GRN</TableHead>
                          <TableHead className="text-right">GRN Qty Added</TableHead>
                          <TableHead className="text-right">Expected After</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {grn.items.map((item, itemIndex) => (
                          <TableRow key={itemIndex}>
                            <TableCell className="font-medium">{item.item_name}</TableCell>
                            <TableCell className="text-right text-muted-foreground">{item.stock_before}</TableCell>
                            <TableCell className="text-right font-medium text-green-600">+{item.grn_quantity}</TableCell>
                            <TableCell className="text-right font-bold">{item.expected_after}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="text-green-600 border-green-600 text-xs">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Added
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            ))}

            {grnWise.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No GRNs to verify
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Final Stock Verification */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5" />
            Final Stock Verification
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Compares total GRN additions with current stock (accounting for transfers, issues, and consumption)
          </p>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Item</TableHead>
                  <TableHead>Store</TableHead>
                  <TableHead className="text-right">From GRN</TableHead>
                  <TableHead className="text-right">Stock Inward</TableHead>
                  <TableHead className="text-right">Transfers In</TableHead>
                  <TableHead className="text-right">Transfers Out</TableHead>
                  <TableHead className="text-right">Consumed</TableHead>
                  <TableHead className="text-right">Expected</TableHead>
                  <TableHead className="text-right">Actual</TableHead>
                  <TableHead className="text-right">Diff</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {finalVerification.map((item, index) => (
                  <TableRow key={index} className={!item.verified ? 'bg-red-50' : ''}>
                    <TableCell className="font-medium">{item.item_name}</TableCell>
                    <TableCell>{item.store_name}</TableCell>
                    <TableCell className="text-right">{item.total_from_grn}</TableCell>
                    <TableCell className="text-right">{item.stock_inward}</TableCell>
                    <TableCell className="text-right text-green-600">+{item.transfers_in}</TableCell>
                    <TableCell className="text-right text-red-600">-{item.transfers_out}</TableCell>
                    <TableCell className="text-right text-amber-600">-{item.consumption}</TableCell>
                    <TableCell className="text-right font-medium">{item.expected_current}</TableCell>
                    <TableCell className="text-right font-bold">{item.actual_current}</TableCell>
                    <TableCell className={`text-right font-bold ${item.difference !== 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {item.difference > 0 ? '+' : ''}{item.difference}
                    </TableCell>
                    <TableCell className="text-center">
                      {item.verified ? (
                        <Badge variant="outline" className="text-xs text-green-600 border-green-600">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="text-xs">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Mismatch
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {finalVerification.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No stock data to verify
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help Text */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <h4 className="font-medium text-blue-800 mb-2">How to Read This Report</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>GRN-wise Section:</strong> Shows each GRN with items added - expand to see stock before/after</li>
            <li>• <strong>Final Verification:</strong> Compares total expected stock vs actual current stock</li>
            <li>• <strong>Expected = </strong> GRN + Stock Inward + Transfers In - Transfers Out - Consumption</li>
            <li>• <strong>Mismatch</strong> indicates stock quantity doesn't match expected (may need investigation)</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export const GRNPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [grns, setGRNs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Check if user is Super Admin
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  
  // Master data for editing
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // View dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [uploadingGrnBill, setUploadingGrnBill] = useState(false);
  const grnBillFileRef = useRef(null);

  const handleGrnBillUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !selectedGRN) return;
    setUploadingGrnBill(true);
    try {
      let last = null;
      for (const file of files) {
        const toUpload = await compressImageFile(file);
        const res = await grnAPI.uploadBill(selectedGRN.id, toUpload);
        last = res.data;
      }
      if (last) {
        setSelectedGRN(prev => ({ ...prev, bill_urls: last.bill_urls, bill_url: last.bill_urls[0] }));
        toast.success('Bill(s) uploaded');
        fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload bill');
    } finally {
      setUploadingGrnBill(false);
      if (grnBillFileRef.current) grnBillFileRef.current.value = '';
    }
  };

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingGRN, setEditingGRN] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [additionalItems, setAdditionalItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(null);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingGRN, setDeletingGRN] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Merge functionality
  const [selectedGRNs, setSelectedGRNs] = useState([]);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  
  // Add new item dialog
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [creatingItem, setCreatingItem] = useState(false);
  const [newItemForm, setNewItemForm] = useState({
    name: '',
    category_id: '',
    unit_id: '',
    brand: '',
    quantity: '',
    rate: ''
  });
  
  // GST entries for editing
  const [gstEntries, setGstEntries] = useState([]);
  
  // Active tab
  const [activeTab, setActiveTab] = useState('list');
  
  // GST Report
  const [gstReport, setGstReport] = useState(null);
  const [gstReportLoading, setGstReportLoading] = useState(false);
  const [gstDateRange, setGstDateRange] = useState({ start: '', end: '' });
  
  // Chart colors
  const GST_COLORS = ['#22c55e', '#f59e0b', '#3b82f6'];

  useEffect(() => {
    fetchData();
    // Check if user is Super Admin
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setIsSuperAdmin(user?.role_name === 'Super Admin');
      }
    } catch (e) {
      console.error('Error parsing user from localStorage:', e);
    }
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [grnsRes, itemsRes, unitsRes, categoriesRes] = await Promise.all([
        grnAPI.getAll({}),
        itemAPI.getAll({ status: 'active' }),
        unitAPI.getAll({ status: 'active' }),
        categoryAPI.getAll({ status: 'active' })
      ]);
      setGRNs(grnsRes.data);
      setItems(itemsRes.data);
      setUnits(unitsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to fetch GRN data');
    } finally {
      setLoading(false);
    }
  };

  const fetchGSTReport = async () => {
    try {
      setGstReportLoading(true);
      const params = {};
      if (gstDateRange.start) params.start_date = gstDateRange.start;
      if (gstDateRange.end) params.end_date = gstDateRange.end;
      const response = await grnAPI.getGSTReport(params);
      setGstReport(response.data);
    } catch (error) {
      toast.error('Failed to fetch GST report');
    } finally {
      setGstReportLoading(false);
    }
  };

  // Fetch GST report when tab changes or date range changes
  useEffect(() => {
    if (activeTab === 'gst-report') {
      fetchGSTReport();
    }
  }, [activeTab]);

  // Auto-open a GRN's view dialog when navigated with ?view=<grn_id> (e.g. from Purchase Orders)
  useEffect(() => {
    const viewId = searchParams.get('view');
    if (viewId && grns.length > 0) {
      const target = grns.find(g => g.id === viewId);
      if (target) {
        handleViewGRN(target);
      }
      searchParams.delete('view');
      setSearchParams(searchParams, { replace: true });
    }
  }, [grns, searchParams]);

  const handleViewGRN = async (grn) => {
    try {
      const response = await grnAPI.getById(grn.id);
      setSelectedGRN(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load GRN details');
    }
  };

  const handleEditGRN = async (grn) => {
    try {
      const response = await grnAPI.getById(grn.id);
      setEditingGRN(response.data);
      setEditItems(response.data.items?.map(item => ({
        item_id: item.item_id,
        item_name: item.item_name,
        unit_id: item.unit_id,
        unit_name: item.unit_name,
        ordered_quantity: item.ordered_quantity,
        previously_received: item.previously_received,
        received_quantity: item.received_quantity,
        rate: item.rate,
        is_additional: item.is_additional || false
      })) || []);
      setAdditionalItems([]);
      // Load GST entries
      setGstEntries(response.data.gst_entries?.map(entry => ({
        percentage: entry.percentage,
        amount: entry.amount
      })) || []);
      setEditDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load GRN for editing');
    }
  };

  const handleEditItemChange = (index, field, value) => {
    const newItems = [...editItems];
    if (field === 'received_quantity' || field === 'rate') {
      newItems[index][field] = parseFloat(value) || 0;
    } else if (field === 'item_id') {
      // Changing the item
      const selectedItem = items.find(i => i.id === value);
      if (selectedItem) {
        newItems[index].item_id = value;
        newItems[index].item_name = selectedItem.name;
        newItems[index].unit_id = selectedItem.default_unit_id;
        newItems[index].unit_name = units.find(u => u.id === selectedItem.default_unit_id)?.name || '';
      }
    } else if (field === 'unit_id') {
      // Changing the unit
      const selectedUnit = units.find(u => u.id === value);
      newItems[index].unit_id = value;
      newItems[index].unit_name = selectedUnit?.name || '';
    } else {
      newItems[index][field] = value;
    }
    setEditItems(newItems);
  };

  // Add existing item from inventory to GRN
  const handleAddExistingItem = () => {
    setAdditionalItems([...additionalItems, {
      item_id: '',
      item_name: '',
      unit_id: '',
      unit_name: '',
      received_quantity: 0,
      rate: 0,
      is_new: false
    }]);
  };

  const handleAdditionalItemChange = (index, field, value) => {
    const newItems = [...additionalItems];
    if (field === 'received_quantity' || field === 'rate') {
      newItems[index][field] = parseFloat(value) || 0;
    } else if (field === 'item_id') {
      const selectedItem = items.find(i => i.id === value);
      if (selectedItem) {
        newItems[index].item_id = value;
        newItems[index].item_name = selectedItem.name;
        newItems[index].unit_id = selectedItem.default_unit_id;
        newItems[index].unit_name = units.find(u => u.id === selectedItem.default_unit_id)?.name || '';
      }
    } else if (field === 'unit_id') {
      const selectedUnit = units.find(u => u.id === value);
      newItems[index].unit_id = value;
      newItems[index].unit_name = selectedUnit?.name || '';
    }
    setAdditionalItems(newItems);
  };

  const handleRemoveAdditionalItem = (index) => {
    setAdditionalItems(additionalItems.filter((_, i) => i !== index));
  };

  // Create new item and add to GRN
  const handleOpenAddItemDialog = () => {
    setNewItemForm({
      name: '',
      category_id: '',
      unit_id: '',
      brand: '',
      quantity: '',
      rate: ''
    });
    setAddItemDialogOpen(true);
  };

  const handleCreateAndAddItem = async () => {
    if (!newItemForm.name || !newItemForm.unit_id || !newItemForm.quantity || !newItemForm.rate) {
      toast.error('Please fill in item name, unit, quantity and rate');
      return;
    }

    try {
      setCreatingItem(true);
      
      // Create the item in the database
      const itemData = {
        name: newItemForm.name,
        category_id: newItemForm.category_id || null,
        default_unit_id: newItemForm.unit_id,
        brand: newItemForm.brand || null,
        status: 'active'
      };
      
      const response = await itemAPI.create(itemData);
      const createdItem = response.data;
      
      // Get unit name
      const unit = units.find(u => u.id === newItemForm.unit_id);
      
      // Add to additional items list
      setAdditionalItems([...additionalItems, {
        item_id: createdItem.id,
        item_name: createdItem.name,
        unit_id: newItemForm.unit_id,
        unit_name: unit?.name || '',
        received_quantity: parseFloat(newItemForm.quantity),
        rate: parseFloat(newItemForm.rate),
        is_new: true
      }]);
      
      // Refresh items list
      const itemsRes = await itemAPI.getAll({ status: 'active' });
      setItems(itemsRes.data);
      
      setAddItemDialogOpen(false);
      toast.success(`Item "${createdItem.name}" created and added`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create item');
    } finally {
      setCreatingItem(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingGRN) return;
    
    try {
      setSaving(true);
      
      // Combine existing items and additional items
      const allItems = [
        ...editItems.map(item => ({
          item_id: item.item_id,
          item_name: item.item_name,
          unit_id: item.unit_id,
          unit_name: item.unit_name,
          received_quantity: item.received_quantity,
          rate: item.rate,
          is_additional: item.is_additional || false
        })),
        ...additionalItems.filter(item => item.item_id).map(item => ({
          item_id: item.item_id,
          item_name: item.item_name,
          unit_id: item.unit_id,
          unit_name: item.unit_name,
          received_quantity: item.received_quantity,
          rate: item.rate,
          is_additional: true
        }))
      ];
      
      // Filter out GST entries with 0 amount
      const validGstEntries = gstEntries.filter(entry => entry.amount > 0);
      
      await grnAPI.update(editingGRN.id, { 
        items: allItems,
        gst_entries: validGstEntries
      });
      toast.success('GRN updated successfully');
      setEditDialogOpen(false);
      setEditingGRN(null);
      setAdditionalItems([]);
      setGstEntries([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update GRN');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async (grn) => {
    try {
      setDownloading(grn.id);
      const response = await grnAPI.downloadPDF(grn.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `GRN_${grn.grn_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(null);
    }
  };

  const handleDeleteGRN = async () => {
    if (!deletingGRN) return;
    
    try {
      setDeleting(true);
      const response = await grnAPI.delete(deletingGRN.id);
      toast.success(response.data.message || `GRN ${deletingGRN.grn_number} deleted successfully. Stock reversed and PO reactivated.`);
      setDeleteDialogOpen(false);
      setDeletingGRN(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete GRN');
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteDialog = (grn) => {
    setDeletingGRN(grn);
    setDeleteDialogOpen(true);
  };

  // Merge GRNs handlers
  const handleSelectGRN = (grnId, checked) => {
    if (checked) {
      setSelectedGRNs([...selectedGRNs, grnId]);
    } else {
      setSelectedGRNs(selectedGRNs.filter(id => id !== grnId));
    }
  };

  const handleSelectAllGRNs = (checked) => {
    if (checked) {
      setSelectedGRNs(grns.map(g => g.id));
    } else {
      setSelectedGRNs([]);
    }
  };

  const canMergeSelectedGRNs = () => {
    if (selectedGRNs.length < 2) return { canMerge: false, reason: 'Select at least 2 GRNs to merge' };
    
    const selectedGrnData = grns.filter(g => selectedGRNs.includes(g.id));
    
    // Check same vendor
    const vendors = [...new Set(selectedGrnData.map(g => g.vendor_id).filter(Boolean))];
    if (vendors.length > 1) return { canMerge: false, reason: 'Cannot merge GRNs from different vendors' };
    
    // Check same store
    const stores = [...new Set(selectedGrnData.map(g => g.store_id))];
    if (stores.length > 1) return { canMerge: false, reason: 'Cannot merge GRNs from different stores' };
    
    return { canMerge: true, reason: null };
  };

  const handleMergeGRNs = async () => {
    try {
      setMerging(true);
      const response = await grnAPI.merge(selectedGRNs);
      toast.success(
        `Merged ${response.data.merged_grns.length + 1} GRNs into ${response.data.primary_grn}` +
        (response.data.merged_pos.length > 0 ? `. POs also merged into ${response.data.primary_po}` : '')
      );
      setMergeDialogOpen(false);
      setSelectedGRNs([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge GRNs');
    } finally {
      setMerging(false);
    }
  };

  const getVarianceBadge = (variance) => {
    switch (variance) {
      case 'matched':
        return <Badge className="bg-green-100 text-green-800">Matched</Badge>;
      case 'short':
        return <Badge className="bg-red-100 text-red-800">Short</Badge>;
      case 'excess':
        return <Badge className="bg-amber-100 text-amber-800">Excess</Badge>;
      case 'not_delivered':
        return <Badge className="bg-gray-100 text-gray-800">Not Delivered</Badge>;
      default:
        return null;
    }
  };

  // GST Entry handlers
  const handleAddGstEntry = () => {
    setGstEntries([...gstEntries, { percentage: 18, amount: 0 }]);
  };

  const handleGstEntryChange = (index, field, value) => {
    const newEntries = [...gstEntries];
    if (field === 'percentage') {
      newEntries[index].percentage = parseInt(value);
    } else if (field === 'amount') {
      newEntries[index].amount = parseFloat(value) || 0;
    }
    setGstEntries(newEntries);
  };

  const handleRemoveGstEntry = (index) => {
    setGstEntries(gstEntries.filter((_, i) => i !== index));
  };

  const calculateGstTotals = () => {
    let gst5 = 0, gst12 = 0, gst18 = 0;
    gstEntries.forEach(entry => {
      if (entry.percentage === 5) gst5 += entry.amount;
      else if (entry.percentage === 12) gst12 += entry.amount;
      else if (entry.percentage === 18) gst18 += entry.amount;
    });
    return { gst5, gst12, gst18, total: gst5 + gst12 + gst18, inputCredit: gst18 };
  };

  const columns = [
    { key: 'grn_number', label: 'GRN #', render: (val) => <span className="font-medium">{val}</span> },
    { key: 'po_number', label: 'PO #' },
    { key: 'vendor_name', label: 'Vendor', render: (val, row) => val || row.custom_vendor_name || '-' },
    { key: 'receipt_date', label: 'Receipt Date', render: (val) => formatDate(val) },
    { key: 'total_value', label: 'Base Value', render: (val) => formatCurrency(val) },
    { 
      key: 'total_gst', 
      label: 'GST', 
      render: (val, row) => {
        const totalGst = val || 0;
        if (totalGst === 0) return <span className="text-muted-foreground">-</span>;
        return (
          <div className="text-right">
            <span className="font-medium">{formatCurrency(totalGst)}</span>
            {row.gst_18 > 0 && (
              <div className="text-xs text-green-600">ITC: {formatCurrency(row.gst_18)}</div>
            )}
          </div>
        );
      }
    },
    { 
      key: 'grand_total', 
      label: 'Total', 
      render: (val, row) => formatCurrency(val || row.total_value || 0)
    },
    { 
      key: 'has_variance', 
      label: 'Variance', 
      render: (val) => val ? (
        <div className="flex items-center gap-1">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <span className="text-amber-600 text-sm">Has Variance</span>
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <span className="text-green-600 text-sm">Matched</span>
        </div>
      )
    },
  ];

  return (
    <div className="space-y-6" data-testid="grn-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6" />
            Goods Receipt Notes (GRN)
          </h1>
          <p className="text-muted-foreground">Track goods received against purchase orders</p>
        </div>
        <Button onClick={() => navigate('/purchase-orders')} data-testid="go-to-po-btn">
          <FileText className="h-4 w-4 mr-2" />
          Go to Purchase Orders
        </Button>
      </div>

      {/* Tabs for GRN List, GST Report, and Stock Verification */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger value="list" className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4" />
            GRN List
          </TabsTrigger>
          <TabsTrigger value="gst-report" className="flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            GST Report
          </TabsTrigger>
          <TabsTrigger value="stock-verify" className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Stock Verification
          </TabsTrigger>
        </TabsList>

        {/* GRN List Tab */}
        <TabsContent value="list" className="space-y-4">
          {/* Merge Selection Bar */}
          {selectedGRNs.length > 0 && (
            <Card className="border-primary">
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="font-medium">{selectedGRNs.length} GRN(s) selected</span>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedGRNs([])}>
                      Clear Selection
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const { canMerge, reason } = canMergeSelectedGRNs();
                      return (
                        <>
                          {!canMerge && reason && (
                            <span className="text-sm text-amber-600 flex items-center gap-1">
                              <AlertTriangle className="h-4 w-4" />
                              {reason}
                            </span>
                          )}
                          <Button 
                            onClick={() => setMergeDialogOpen(true)} 
                            disabled={!canMerge}
                            data-testid="merge-grns-btn"
                          >
                            <Merge className="h-4 w-4 mr-2" />
                            Merge Selected GRNs
                          </Button>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* GRN List with DataTable */}
          <Card>
            <CardContent className="pt-6">
              {/* Select All Checkbox */}
              <div className="flex items-center gap-2 mb-4 pb-4 border-b">
                <Checkbox
                  checked={selectedGRNs.length === grns.length && grns.length > 0}
                  onCheckedChange={handleSelectAllGRNs}
                  data-testid="select-all-grns"
                />
                <span className="text-sm text-muted-foreground">
                  Select all ({grns.length} GRNs)
                </span>
              </div>
              
              <DataTable
                module="grn"
                data={grns}
                loading={loading}
                title="Goods Receipt Notes"
                columns={columns}
                renderActions={(grn) => (
                  <div className="flex justify-end gap-1 items-center">
                    <Checkbox
                      checked={selectedGRNs.includes(grn.id)}
                      onCheckedChange={(checked) => handleSelectGRN(grn.id, checked)}
                      className="mr-2"
                      data-testid={`select-grn-${grn.id}`}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleViewGRN(grn)}
                      title="View Details"
                      data-testid={`view-grn-${grn.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEditGRN(grn)}
                      title="Edit GRN"
                      data-testid={`edit-grn-${grn.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDownloadPDF(grn)}
                      disabled={downloading === grn.id}
                      title="Download PDF"
                      data-testid={`download-grn-${grn.id}`}
                    >
                      {downloading === grn.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                    </Button>
                    {isSuperAdmin && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDeleteDialog(grn)}
                        title="Delete GRN (Super Admin Only)"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid={`delete-grn-${grn.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )}
          />
        </CardContent>
      </Card>
        </TabsContent>

        {/* GST Report Tab */}
        <TabsContent value="gst-report" className="space-y-6">
          {/* Date Range Filter */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Filter by Date Range
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-end gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={gstDateRange.start}
                    onChange={(e) => setGstDateRange({ ...gstDateRange, start: e.target.value })}
                    data-testid="gst-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={gstDateRange.end}
                    onChange={(e) => setGstDateRange({ ...gstDateRange, end: e.target.value })}
                    data-testid="gst-end-date"
                  />
                </div>
                <Button onClick={fetchGSTReport} disabled={gstReportLoading} data-testid="apply-gst-filter">
                  {gstReportLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Apply Filter
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => { setGstDateRange({ start: '', end: '' }); fetchGSTReport(); }}
                >
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>

          {gstReportLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : gstReport ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-green-100 rounded-lg">
                        <IndianRupee className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">GST @ 5%</p>
                        <p className="text-2xl font-bold">{formatCurrency(gstReport.summary.total_gst_5)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-amber-100 rounded-lg">
                        <IndianRupee className="h-6 w-6 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">GST @ 12%</p>
                        <p className="text-2xl font-bold">{formatCurrency(gstReport.summary.total_gst_12)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <IndianRupee className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">GST @ 18%</p>
                        <p className="text-2xl font-bold">{formatCurrency(gstReport.summary.total_gst_18)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-green-500 bg-green-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-green-200 rounded-lg">
                        <TrendingUp className="h-6 w-6 text-green-700" />
                      </div>
                      <div>
                        <p className="text-sm text-green-700 font-medium">Input Credit Eligible</p>
                        <p className="text-2xl font-bold text-green-800">{formatCurrency(gstReport.summary.total_input_credit_eligible)}</p>
                        <p className="text-xs text-green-600">(18% GST Only)</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Totals Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Total Base Value</p>
                    <p className="text-2xl font-bold">{formatCurrency(gstReport.summary.total_value)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Total GST</p>
                    <p className="text-2xl font-bold">{formatCurrency(gstReport.summary.total_gst)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-primary/5 border-primary">
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Grand Total (incl. GST)</p>
                    <p className="text-2xl font-bold text-primary">{formatCurrency(gstReport.summary.total_grand)}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* GST Distribution Pie Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle>GST Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {gstReport.summary.total_gst > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'GST 5%', value: gstReport.summary.total_gst_5 },
                              { name: 'GST 12%', value: gstReport.summary.total_gst_12 },
                              { name: 'GST 18% (ITC)', value: gstReport.summary.total_gst_18 }
                            ].filter(d => d.value > 0)}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {[
                              { name: 'GST 5%', value: gstReport.summary.total_gst_5 },
                              { name: 'GST 12%', value: gstReport.summary.total_gst_12 },
                              { name: 'GST 18% (ITC)', value: gstReport.summary.total_gst_18 }
                            ].filter(d => d.value > 0).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={GST_COLORS[index % GST_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                        No GST data available
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Monthly GST Trend */}
                <Card>
                  <CardHeader>
                    <CardTitle>Monthly GST Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {gstReport.monthly_breakdown?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={gstReport.monthly_breakdown}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                          <Bar dataKey="gst_5" name="GST 5%" fill="#22c55e" stackId="a" />
                          <Bar dataKey="gst_12" name="GST 12%" fill="#f59e0b" stackId="a" />
                          <Bar dataKey="gst_18" name="GST 18%" fill="#3b82f6" stackId="a" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                        No monthly data available
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* GRN Details Table */}
              <Card>
                <CardHeader>
                  <CardTitle>GRNs with GST ({gstReport.summary.grn_count} records)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>GRN #</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Vendor</TableHead>
                          <TableHead className="text-right">Base Value</TableHead>
                          <TableHead className="text-right">GST 5%</TableHead>
                          <TableHead className="text-right">GST 12%</TableHead>
                          <TableHead className="text-right">GST 18%</TableHead>
                          <TableHead className="text-right">Total GST</TableHead>
                          <TableHead className="text-right">Grand Total</TableHead>
                          <TableHead className="text-right text-green-700">Input Credit</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {gstReport.grns?.length > 0 ? (
                          gstReport.grns.map((grn) => (
                            <TableRow key={grn.id}>
                              <TableCell className="font-medium">{grn.grn_number}</TableCell>
                              <TableCell>{formatDate(grn.receipt_date)}</TableCell>
                              <TableCell>{grn.vendor_name || grn.custom_vendor_name || '-'}</TableCell>
                              <TableCell className="text-right">{formatCurrency(grn.total_value)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(grn.gst_5 || 0)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(grn.gst_12 || 0)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(grn.gst_18 || 0)}</TableCell>
                              <TableCell className="text-right font-medium">{formatCurrency(grn.total_gst || 0)}</TableCell>
                              <TableCell className="text-right font-medium">{formatCurrency(grn.grand_total || grn.total_value)}</TableCell>
                              <TableCell className="text-right font-medium text-green-700">
                                {formatCurrency(grn.input_credit_eligible || 0)}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                              No GRNs with GST entries found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No GST data available. Add GST entries to GRNs to see the report.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Stock Verification Tab */}
        <TabsContent value="stock-verify" className="space-y-6">
          <StockVerificationTab />
        </TabsContent>
      </Tabs>

      {/* View GRN Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[1200px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <ClipboardCheck className="h-5 w-5" />
              GRN Details - {selectedGRN?.grn_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedGRN && (
            <div className="space-y-6">
              {/* Summary Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 p-4 md:p-6 bg-muted rounded-lg">
                <div>
                  <span className="text-sm text-muted-foreground">PO Number</span>
                  <p className="font-medium">{selectedGRN.po_number}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Vendor</span>
                  <p className="font-medium">{selectedGRN.vendor_name || selectedGRN.custom_vendor_name}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Receipt Date</span>
                  <p className="font-medium">{formatDate(selectedGRN.receipt_date)}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Received By</span>
                  <p className="font-medium">{selectedGRN.received_by_name}</p>
                </div>
              </div>

              {/* Bill Info */}
              {(() => {
                const grnBills = (selectedGRN.bill_urls?.length > 0)
                  ? selectedGRN.bill_urls
                  : (selectedGRN.bill_url ? [selectedGRN.bill_url] : []);
                return (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium flex items-center gap-2 mb-2">
                      <FileText className="h-4 w-4 text-blue-600" />
                      Bill/Invoice Details
                    </h4>
                    {selectedGRN.bill_number && (
                      <div className="text-sm mb-2">
                        <span className="text-muted-foreground">Bill Number: </span>
                        <span className="font-medium">{selectedGRN.bill_number}</span>
                      </div>
                    )}
                    {grnBills.length > 0 ? (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {grnBills.map((url, idx) => (
                          <div key={idx} className="flex items-center gap-1 bg-white border rounded-md px-2 py-1" data-testid={`grn-bill-view-${idx}`}>
                            <span className="text-xs text-muted-foreground">Bill {idx + 1}</span>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => viewBill(url)} title="View"><Eye className="h-3.5 w-3.5 text-blue-600" /></Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => downloadBill(url, `bill_${selectedGRN.grn_number}_${idx + 1}`)} title="Download"><Download className="h-3.5 w-3.5 text-green-600" /></Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground mb-2">No bills attached yet.</p>
                    )}
                    <input
                      type="file"
                      ref={grnBillFileRef}
                      onChange={handleGrnBillUpload}
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
                      multiple
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => grnBillFileRef.current?.click()}
                      disabled={uploadingGrnBill}
                      data-testid="grn-upload-bill-btn"
                    >
                      {uploadingGrnBill ? (
                        <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Uploading...</>
                      ) : (
                        <><Upload className="h-4 w-4 mr-1" />{grnBills.length > 0 ? 'Add More Bills' : 'Upload Bill'}</>
                      )}
                    </Button>
                  </div>
                );
              })()}

              {/* Variance Summary */}
              {selectedGRN.has_variance && selectedGRN.variance_summary && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    Variance Summary
                  </h4>
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-600">
                      Matched: {selectedGRN.variance_summary.matched}
                    </span>
                    <span className="text-red-600">
                      Short: {selectedGRN.variance_summary.short}
                    </span>
                    <span className="text-amber-600">
                      Excess: {selectedGRN.variance_summary.excess}
                    </span>
                    <span className="text-gray-600">
                      Not Delivered: {selectedGRN.variance_summary.not_delivered}
                    </span>
                  </div>
                </div>
              )}

              {/* Items Table */}
              <div>
                <h4 className="font-medium mb-2">Items Received</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Ordered</TableHead>
                      <TableHead className="text-right">Prev. Received</TableHead>
                      <TableHead className="text-right">This GRN</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedGRN.items?.map((item, idx) => (
                      <TableRow 
                        key={idx}
                        className={
                          item.variance === 'short' ? 'bg-red-50' :
                          item.variance === 'excess' ? 'bg-amber-50' :
                          item.variance === 'not_delivered' ? 'bg-gray-50' :
                          'bg-green-50'
                        }
                      >
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell className="text-right">{item.ordered_quantity} {item.unit_name}</TableCell>
                        <TableCell className="text-right">{item.previously_received}</TableCell>
                        <TableCell className="text-right font-medium">{item.received_quantity}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.rate)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                        <TableCell>{getVarianceBadge(item.variance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-80 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Items Ordered:</span>
                    <span>{selectedGRN.items_ordered || selectedGRN.items?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Items Received:</span>
                    <span>{selectedGRN.items_received || selectedGRN.items?.filter(i => i.received_quantity > 0).length || 0}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-muted-foreground">Base Value:</span>
                    <span>{formatCurrency(selectedGRN.total_value)}</span>
                  </div>
                  
                  {/* GST Breakdown */}
                  {(selectedGRN.gst_5 > 0 || selectedGRN.gst_12 > 0 || selectedGRN.gst_18 > 0) && (
                    <>
                      {selectedGRN.gst_5 > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">GST @ 5%:</span>
                          <span>{formatCurrency(selectedGRN.gst_5)}</span>
                        </div>
                      )}
                      {selectedGRN.gst_12 > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">GST @ 12%:</span>
                          <span>{formatCurrency(selectedGRN.gst_12)}</span>
                        </div>
                      )}
                      {selectedGRN.gst_18 > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">GST @ 18%:</span>
                          <span>{formatCurrency(selectedGRN.gst_18)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm font-medium">
                        <span className="text-muted-foreground">Total GST:</span>
                        <span>{formatCurrency(selectedGRN.total_gst)}</span>
                      </div>
                      {selectedGRN.gst_18 > 0 && (
                        <div className="flex justify-between text-sm text-green-700 bg-green-50 p-1 rounded">
                          <span>Input Credit Eligible:</span>
                          <span className="font-medium">{formatCurrency(selectedGRN.input_credit_eligible)}</span>
                        </div>
                      )}
                    </>
                  )}
                  
                  <div className="flex justify-between font-medium text-lg border-t pt-2">
                    <span>Grand Total:</span>
                    <span>{formatCurrency(selectedGRN.grand_total || selectedGRN.total_value)}</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedGRN.notes && (
                <div>
                  <h4 className="font-medium mb-1">Notes</h4>
                  <p className="text-muted-foreground">{selectedGRN.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit GRN Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[1200px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Pencil className="h-5 w-5" />
              Edit GRN - {editingGRN?.grn_number}
            </DialogTitle>
            <DialogDescription>
              Edit items, change quantities/rates, or add new items. Changes will recalculate totals and adjust stock.
            </DialogDescription>
          </DialogHeader>
          
          {editingGRN && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm p-4 md:p-6 bg-muted rounded-lg">
                <div>
                  <span className="text-muted-foreground">PO Number:</span>{' '}
                  <span className="font-medium">{editingGRN.po_number}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Receipt Date:</span>{' '}
                  <span className="font-medium">{formatDate(editingGRN.receipt_date)}</span>
                </div>
              </div>

              {/* Existing Items */}
              <div>
                <h4 className="font-medium mb-2">Items from PO</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[30%]">Item</TableHead>
                      <TableHead className="w-[15%]">Unit</TableHead>
                      <TableHead className="text-right w-[15%]">Ordered</TableHead>
                      <TableHead className="text-right w-[15%]">Received Qty</TableHead>
                      <TableHead className="text-right w-[15%]">Rate (₹)</TableHead>
                      <TableHead className="text-right w-[10%]">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {editItems.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <SearchableItemSelect
                            items={items}
                            value={item.item_id}
                            onSelect={(value) => handleEditItemChange(idx, 'item_id', value)}
                            placeholder="Select item..."
                            testId={`edit-grn-item-${idx}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={item.unit_id || "none"}
                            onValueChange={(value) => handleEditItemChange(idx, 'unit_id', value === "none" ? "" : value)}
                          >
                            <SelectTrigger className="h-9" data-testid={`edit-grn-unit-${idx}`}>
                              <SelectValue placeholder="Unit" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Select</SelectItem>
                              {units.map(unit => (
                                <SelectItem key={unit.id} value={unit.id}>{unit.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {item.ordered_quantity || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.received_quantity}
                            onChange={(e) => handleEditItemChange(idx, 'received_quantity', e.target.value)}
                            className="w-24 h-9 text-right"
                            data-testid={`edit-grn-qty-${idx}`}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.rate}
                            onChange={(e) => handleEditItemChange(idx, 'rate', e.target.value)}
                            className="w-24 h-9 text-right"
                            data-testid={`edit-grn-rate-${idx}`}
                          />
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(item.received_quantity * item.rate)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Additional Items Section */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-medium">Additional Items (Not in Original PO)</h4>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleAddExistingItem} data-testid="add-existing-item-btn">
                      <Plus className="h-4 w-4 mr-1" />
                      Add from Inventory
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleOpenAddItemDialog} data-testid="create-new-item-btn">
                      <Plus className="h-4 w-4 mr-1" />
                      Create New Item
                    </Button>
                  </div>
                </div>
                
                {additionalItems.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[30%]">Item</TableHead>
                        <TableHead className="w-[15%]">Unit</TableHead>
                        <TableHead className="text-right w-[15%]">Received Qty</TableHead>
                        <TableHead className="text-right w-[15%]">Rate (₹)</TableHead>
                        <TableHead className="text-right w-[15%]">Amount</TableHead>
                        <TableHead className="w-[10%]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {additionalItems.map((item, idx) => (
                        <TableRow key={idx} className="bg-blue-50">
                          <TableCell>
                            {item.is_new ? (
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{item.item_name}</span>
                                <Badge className="bg-blue-100 text-blue-700 text-xs">New</Badge>
                              </div>
                            ) : (
                              <SearchableItemSelect
                                items={items}
                                value={item.item_id}
                                onSelect={(value) => handleAdditionalItemChange(idx, 'item_id', value)}
                                placeholder="Select item..."
                                testId={`additional-item-${idx}`}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {item.is_new ? (
                              <span>{item.unit_name}</span>
                            ) : (
                              <Select
                                value={item.unit_id || "none"}
                                onValueChange={(value) => handleAdditionalItemChange(idx, 'unit_id', value === "none" ? "" : value)}
                              >
                                <SelectTrigger className="h-9">
                                  <SelectValue placeholder="Unit" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Select</SelectItem>
                                  {units.map(unit => (
                                    <SelectItem key={unit.id} value={unit.id}>{unit.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.received_quantity}
                              onChange={(e) => handleAdditionalItemChange(idx, 'received_quantity', e.target.value)}
                              className="w-24 h-9 text-right"
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.rate}
                              onChange={(e) => handleAdditionalItemChange(idx, 'rate', e.target.value)}
                              className="w-24 h-9 text-right"
                            />
                          </TableCell>
                          <TableCell className="text-right font-medium text-blue-600">
                            {formatCurrency(item.received_quantity * item.rate)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveAdditionalItem(idx)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                
                {additionalItems.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No additional items. Use buttons above to add items not in the original PO.
                  </p>
                )}
              </div>

              {/* GST Entries Section */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Receipt className="h-4 w-4" />
                    GST Entries
                  </h4>
                  <Button variant="outline" size="sm" onClick={handleAddGstEntry} data-testid="add-gst-entry-btn">
                    <Plus className="h-4 w-4 mr-1" />
                    Add GST
                  </Button>
                </div>
                
                {gstEntries.length > 0 ? (
                  <div className="space-y-3">
                    {gstEntries.map((entry, idx) => (
                      <div key={idx} className="flex items-center gap-4 p-3 bg-muted rounded-lg">
                        <div className="flex-1">
                          <Label className="text-sm">GST Percentage</Label>
                          <Select
                            value={entry.percentage.toString()}
                            onValueChange={(value) => handleGstEntryChange(idx, 'percentage', value)}
                          >
                            <SelectTrigger className="w-full mt-1" data-testid={`gst-percent-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="5">5%</SelectItem>
                              <SelectItem value="12">12%</SelectItem>
                              <SelectItem value="18">18% (Input Credit Eligible)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex-1">
                          <Label className="text-sm">GST Amount (₹)</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={entry.amount}
                            onChange={(e) => handleGstEntryChange(idx, 'amount', e.target.value)}
                            className="mt-1"
                            data-testid={`gst-amount-${idx}`}
                          />
                        </div>
                        {entry.percentage === 18 && entry.amount > 0 && (
                          <Badge className="bg-green-100 text-green-800 self-end mb-2">ITC Eligible</Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveGstEntry(idx)}
                          className="text-destructive self-end"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    
                    {/* GST Summary */}
                    {(() => {
                      const gstTotals = calculateGstTotals();
                      return (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mt-3">
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                            <div>
                              <span className="text-muted-foreground">GST 5%:</span>
                              <span className="font-medium ml-2">{formatCurrency(gstTotals.gst5)}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">GST 12%:</span>
                              <span className="font-medium ml-2">{formatCurrency(gstTotals.gst12)}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">GST 18%:</span>
                              <span className="font-medium ml-2">{formatCurrency(gstTotals.gst18)}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Total GST:</span>
                              <span className="font-bold ml-2">{formatCurrency(gstTotals.total)}</span>
                            </div>
                            <div className="bg-green-100 px-2 py-1 rounded">
                              <span className="text-green-700">Input Credit:</span>
                              <span className="font-bold text-green-800 ml-2">{formatCurrency(gstTotals.inputCredit)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No GST entries. Click "Add GST" to add GST amounts from the bill.
                  </p>
                )}
              </div>

              {/* Grand Total */}
              {(() => {
                const baseTotal = editItems.reduce((sum, item) => sum + (item.received_quantity * item.rate), 0) +
                                  additionalItems.reduce((sum, item) => sum + (item.received_quantity * item.rate), 0);
                const gstTotals = calculateGstTotals();
                const grandTotal = baseTotal + gstTotals.total;
                return (
                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Base Amount:</span>
                      <span>{formatCurrency(baseTotal)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Total GST:</span>
                      <span>{formatCurrency(gstTotals.total)}</span>
                    </div>
                    <div className="flex justify-between items-center text-lg font-medium pt-2 border-t">
                      <span>Grand Total (Bill Amount):</span>
                      <span className="text-primary text-xl">{formatCurrency(grandTotal)}</span>
                    </div>
                    {gstTotals.inputCredit > 0 && (
                      <div className="flex justify-between items-center text-sm text-green-700 bg-green-50 p-2 rounded">
                        <span>Input Credit Eligible (18% GST):</span>
                        <span className="font-medium">{formatCurrency(gstTotals.inputCredit)}</span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-grn-edit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Item Dialog */}
      <Dialog open={addItemDialogOpen} onOpenChange={setAddItemDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Plus className="h-5 w-5" />
              Create New Item
            </DialogTitle>
            <DialogDescription>
              Create a new item and add it to this GRN. The item will be saved to your inventory.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Item Name *</Label>
              <Input
                placeholder="Enter item name"
                value={newItemForm.name}
                onChange={(e) => setNewItemForm({ ...newItemForm, name: e.target.value })}
                data-testid="new-item-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={newItemForm.category_id || "none"}
                  onValueChange={(value) => setNewItemForm({ ...newItemForm, category_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Category</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Unit *</Label>
                <Select
                  value={newItemForm.unit_id || "none"}
                  onValueChange={(value) => setNewItemForm({ ...newItemForm, unit_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select Unit</SelectItem>
                    {units.map(unit => (
                      <SelectItem key={unit.id} value={unit.id}>{unit.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Brand (Optional)</Label>
              <Input
                placeholder="Enter brand name"
                value={newItemForm.brand}
                onChange={(e) => setNewItemForm({ ...newItemForm, brand: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  value={newItemForm.quantity}
                  onChange={(e) => setNewItemForm({ ...newItemForm, quantity: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Rate (₹) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={newItemForm.rate}
                  onChange={(e) => setNewItemForm({ ...newItemForm, rate: e.target.value })}
                />
              </div>
            </div>
            
            {newItemForm.quantity && newItemForm.rate && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Amount</p>
                <p className="text-xl font-bold text-primary">
                  {formatCurrency(parseFloat(newItemForm.quantity || 0) * parseFloat(newItemForm.rate || 0))}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddItemDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateAndAddItem} disabled={creatingItem}>
              {creatingItem ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create & Add
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Confirmation Dialog */}
      <AlertDialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Merge className="h-5 w-5" />
              Merge {selectedGRNs.length} GRNs
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                You are about to merge the following GRNs:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm max-h-32 overflow-auto">
                {grns.filter(g => selectedGRNs.includes(g.id)).map(g => (
                  <li key={g.id}>
                    <span className="font-medium">{g.grn_number}</span> - {g.vendor_name || g.custom_vendor_name} ({formatCurrency(g.total_value)})
                  </li>
                ))}
              </ul>
              <div className="bg-muted p-3 rounded-lg space-y-2 text-sm">
                <p className="font-medium">This action will:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Combine all items into the <strong>oldest GRN</strong></li>
                  <li>Delete the other GRN records</li>
                  <li>Merge the linked Purchase Orders (if different)</li>
                  <li>Update stock records to reference the merged GRN</li>
                </ul>
              </div>
              <p className="text-amber-600 font-medium">
                This action cannot be undone.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={merging}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMergeGRNs}
              disabled={merging}
              data-testid="confirm-merge-grns"
            >
              {merging ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Merging...
                </>
              ) : (
                <>
                  <Merge className="h-4 w-4 mr-2" />
                  Merge GRNs
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete GRN - {deletingGRN?.grn_number}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Are you sure you want to delete this GRN? This action will:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Reverse all stock quantities received from this GRN</li>
                <li>Delete associated stock inward entries</li>
                <li>Reactivate the linked Purchase Order ({deletingGRN?.po_number})</li>
              </ul>
              <p className="font-medium text-destructive">
                This action cannot be undone.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteGRN}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-grn"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete GRN
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GRNPage;
